import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function MustTryVancouverFoodFestivals2025() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-food-festivals-2025";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-dine-out": "Dine Out Vancouver Festival",
    "section-hot-chocolate": "Greater Vancouver Hot Chocolate Festival",
    "section-wine-fest": "Vancouver International Wine Festival",
    "section-sakura": "Sakura Days Japan Fair",
    "section-spot-prawn": "Spot Prawn Festival",
    "section-greek-day": "Greek Day on Broadway",
    "section-italian-day": "Italian Day on The Drive",
    "section-powell-street": "Powell Street Festival",
    "section-richmond-night-market": "Richmond Night Market",
    "section-christmas-market": "Vancouver Christmas Market",
    tips: "Tips for Attendees",
    "local-regulations": "Local Regulations & Safety",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Sample references + disclaimers from the conversation
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Events & Festivals",
    },
    {
      url: "https://dineoutvancouver.com/",
      label: "Dine Out Vancouver Festival – Official Website",
    },
    {
      url: "https://vanwinefest.ca/",
      label: "Vancouver International Wine Festival – Official Site",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location references an ID used in the article sections.
  // Coordinates must match exactly what's used in the final text's <Marker> calls.
  const locations = [
    // Dine Out Vancouver (approx. location at Downtown Vancouver)
    {
      id: "section-dine-out",
      lat: 49.28270,
      lng: -123.12070,
      popupText: "Dine Out Vancouver Festival – Downtown Vancouver",
    },
    // Hot Chocolate Festival (downtown approximation)
    {
      id: "section-hot-chocolate",
      lat: 49.28191,
      lng: -123.11874,
      popupText: "Greater Vancouver Hot Chocolate Festival – Various Spots",
    },
    // Vancouver Wine Festival (Vancouver Convention Centre)
    {
      id: "section-wine-fest",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "Vancouver Intl. Wine Festival – Convention Centre West",
    },
    // Sakura Days Japan Fair (VanDusen Garden)
    {
      id: "section-sakura",
      lat: 49.23856,
      lng: -123.12872,
      popupText: "Sakura Days Japan Fair – VanDusen Botanical Garden",
    },
    // Spot Prawn Festival (False Creek Fishermen’s Wharf)
    {
      id: "section-spot-prawn",
      lat: 49.27050,
      lng: -123.13999,
      popupText: "Spot Prawn Festival – False Creek Fishermen’s Wharf",
    },
    // Greek Day on Broadway (approx. center in Kitsilano)
    {
      id: "section-greek-day",
      lat: 49.26450,
      lng: -123.17200,
      popupText: "Greek Day on Broadway – West Broadway, Kitsilano",
    },
    // Italian Day (Commercial Drive)
    {
      id: "section-italian-day",
      lat: 49.27448,
      lng: -123.06945,
      popupText: "Italian Day on The Drive – Commercial Drive",
    },
    // Powell Street Festival (Oppenheimer Park)
    {
      id: "section-powell-street",
      lat: 49.28268,
      lng: -123.09425,
      popupText: "Powell Street Festival – Oppenheimer Park (Japantown)",
    },
    // Richmond Night Market
    {
      id: "section-richmond-night-market",
      lat: 49.19561,
      lng: -123.12594,
      popupText: "Richmond Night Market – 8351 River Road, Richmond",
    },
    // Vancouver Christmas Market (Jack Poole Plaza)
    {
      id: "section-christmas-market",
      lat: 49.28763,
      lng: -123.11300,
      popupText: "Vancouver Christmas Market – Jack Poole Plaza",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>{ref_data?.title || "Vancouver Food Festivals 2025"}</title>
        <meta
          name="description"
          content={ref_data?.excerpt || "Explore Vancouver's must-try food festivals in 2025"}
        />
        <meta
          name="keywords"
          content="vancouver, food, festivals, 2025, must-try, events"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://www.bcouver.com/articles/${ref_id}.html`}
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content={ref_data?.title || "Vancouver Food Festivals 2025"}
        />
        <meta
          property="og:description"
          content={ref_data?.excerpt || "Explore Vancouver's must-try food festivals in 2025"}
        />
        <meta
          property="og:image"
          content={`https://bcouver.com/assets/images/${ref_id}.jpg`}
        />
        <meta
          property="og:url"
          content={`https://www.bcouver.com/articles/${ref_id}.html`}
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={ref_data?.title || "Vancouver Food Festivals 2025"}
        />
        <meta
          name="twitter:description"
          content={ref_data?.excerpt || "Explore Vancouver's must-try food festivals in 2025"}
        />
        <meta
          name="twitter:image"
          content={`https://bcouver.com/assets/images/${ref_id}.jpg`}
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt={ref_data?.["image-desc"] || "Vancouver Food Festival 2025"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>Introduction</h2>
            <p>
              Vancouver’s culinary scene is overflowing with diversity and flavor. 
              From street food celebrations to upscale dining extravaganzas, the city’s food 
              festivals are a must-attend experience for any foodie. In 2025, Vancouver is 
              serving up a year-long menu of festivals that showcase its multicultural cuisine, 
              fresh local ingredients, and innovative chefs. These events aren’t just about 
              eating – they’re about celebrating community, culture, and the joy of food.
            </p>

            <p>
              In this guide, we’ll walk you through the can’t-miss food festivals in Vancouver 
              for 2025. You’ll find key details – when and where they’re happening, what to 
              expect, and insider tips to make the most of your visit.
            </p>

            {/* Dine Out Vancouver Festival */}
            <h2 id="section-dine-out" ref={(el) => setSpotRef("section-dine-out", el)}>
              Dine Out Vancouver Festival
            </h2>
            <p>
              <strong>Dates & Hours:</strong> January 22 – February 9, 2025, primarily dinner times. 
              Over 350 restaurants across Metro Vancouver offer special multi-course menus at 
              discounted prices ranging from about $20 to $65 per person. Reservations are 
              recommended, especially for popular spots.
            </p>
            <p>
              <strong>Location:</strong> City-wide, but centered around Downtown Vancouver{" "}
              <Marker
                coordY={49.28270}
                coordX={-123.12070}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              . Enjoy everything from hidden neighborhood gems to upscale dining. 
            </p>
            <p>
              <strong>What It’s About:</strong> Canada’s largest restaurant festival, a chance to 
              sample the city’s best eateries, plus events like Street Food City, guided restaurant 
              tours, cooking classes, and more.
            </p>
            <p>
              <strong>Insider Tip:</strong> Browse the Dine Out website and book your restaurant 
              choices early – prime dining times fill up fast. Plan a mix of high-end and casual 
              spots to experience the full culinary spectrum.
            </p>

            {/* Greater Vancouver Hot Chocolate Festival */}
            <h2 id="section-hot-chocolate" ref={(el) => setSpotRef("section-hot-chocolate", el)}>
              Greater Vancouver Hot Chocolate Festival
            </h2>
            <p>
              <strong>Dates & Hours:</strong> January 18 – February 14, 2025. Hours vary by vendor. 
              Expect dozens of cafes, bakeries, and chocolate shops citywide offering unique and 
              creative hot chocolate flavors.
            </p>
            <p>
              <strong>Location:</strong> Various spots in Metro Vancouver{" "}
              <Marker
                coordY={49.28191}
                coordX={-123.11874}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              . This is a city-wide crawl where you purchase drinks from each participating location.
            </p>
            <p>
              <strong>What It’s About:</strong> A winter-long celebration of creativity in a cup, 
              featuring imaginative flavors, vegan/dairy-free options, and pairing treats (like 
              cookies or pastries).
            </p>
            <p>
              <strong>Insider Tip:</strong> Plan a route to avoid sugar overload. Weekdays or 
              off-peak hours help you skip the lines. Share drinks with a friend to taste even 
              more flavors.
            </p>

            {/* Vancouver International Wine Festival */}
            <h2 id="section-wine-fest" ref={(el) => setSpotRef("section-wine-fest", el)}>
              Vancouver International Wine Festival
            </h2>
            <p>
              <strong>Dates & Hours:</strong> February 22 – March 2, 2025. The main event is the 
              International Festival Tasting at the Vancouver Convention Centre.
            </p>
            <p>
              <strong>Location:</strong> Vancouver Convention Centre West{" "}
              <Marker
                coordY={49.28880}
                coordX={-123.11125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , plus select restaurants around the city.
            </p>
            <p>
              <strong>What It’s About:</strong> A world-class wine and food extravaganza. Hundreds 
              of wines under one roof, wine dinners at local restaurants, educational seminars, and 
              more.
            </p>
            <p>
              <strong>Insider Tip:</strong> Buy tickets early; popular tasting sessions sell out. 
              Pace yourself and stay hydrated. Use public transit or arrange a safe ride home.
            </p>

            {/* Sakura Days Japan Fair */}
            <h2 id="section-sakura" ref={(el) => setSpotRef("section-sakura", el)}>
              Sakura Days Japan Fair
            </h2>
            <p>
              <strong>Dates & Hours:</strong> April 12 – 13, 2025, at VanDusen Botanical Garden.
            </p>
            <p>
              <strong>Location:</strong> VanDusen Botanical Garden{" "}
              <Marker
                coordY={49.23856}
                coordX={-123.12872}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              . The festival transforms the garden into a slice of Japan with tea ceremonies, taiko 
              drumming, and traditional dance.
            </p>
            <p>
              <strong>What It’s About:</strong> Part of the Vancouver Cherry Blossom Festival, 
              celebrating Japanese culture, food (from ramen to matcha treats), and springtime 
              cherry blossoms.
            </p>
            <p>
              <strong>Insider Tip:</strong> Buy tickets online to save money and skip the gate line. 
              Parking is limited, so consider public transit or arrive early.
            </p>

            {/* Spot Prawn Festival */}
            <h2 id="section-spot-prawn" ref={(el) => setSpotRef("section-spot-prawn", el)}>
              Spot Prawn Festival
            </h2>
            <p>
              <strong>Dates & Hours:</strong> Late May 2025 (usually the last Sunday in May), 11am – 3:30pm.
            </p>
            <p>
              <strong>Location:</strong> False Creek Fishermen’s Wharf{" "}
              <Marker
                coordY={49.27050}
                coordX={-123.13999}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , near Granville Island. 
            </p>
            <p>
              <strong>What It’s About:</strong> Celebrating BC’s iconic wild spot prawns at the 
              start of their short-but-sweet season. Enjoy fresh-off-the-boat prawns, chef 
              demonstrations, and spot prawn boils.
            </p>
            <p>
              <strong>Insider Tip:</strong> Arrive early to buy the freshest prawns. Bring a cooler 
              if you plan to take some home. It’s an outdoor event, so dress for the weather.
            </p>

            {/* Greek Day on Broadway */}
            <h2 id="section-greek-day" ref={(el) => setSpotRef("section-greek-day", el)}>
              Greek Day on Broadway
            </h2>
            <p>
              <strong>Dates & Hours:</strong> June 22, 2025 (Sunday), 11:00am – 9:00pm.
            </p>
            <p>
              <strong>Location:</strong> West Broadway, Kitsilano{" "}
              <Marker
                coordY={49.26450}
                coordX={-123.17200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , between Macdonald and Blenheim. Free admission.
            </p>
            <p>
              <strong>What It’s About:</strong> A vibrant street party celebrating Greek culture 
              through food (souvlaki, gyro, loukoumades), music, dancing, and community spirit.
            </p>
            <p>
              <strong>Insider Tip:</strong> Go early to avoid the largest crowds. Public transit is 
              recommended as parking is limited.
            </p>

            {/* Italian Day on The Drive */}
            <h2 id="section-italian-day" ref={(el) => setSpotRef("section-italian-day", el)}>
              Italian Day on The Drive
            </h2>
            <p>
              <strong>Dates & Hours:</strong> June 8, 2025 (Sunday), roughly Noon – 8:00pm.
            </p>
            <p>
              <strong>Location:</strong> Commercial Drive{" "}
              <Marker
                coordY={49.27448}
                coordX={-123.06945}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , from Venables St. to Grandview Hwy. Free admission.
            </p>
            <p>
              <strong>What It’s About:</strong> A massive cultural street festival celebrating 
              Italian heritage. Feast on pizza, pasta, gelato, arancini, and more while enjoying 
              live music and family activities.
            </p>
            <p>
              <strong>Insider Tip:</strong> Skip driving if you can. Check the event schedule for 
              special performances or contests. Bring cash for smaller vendors.
            </p>

            {/* Powell Street Festival */}
            <h2 id="section-powell-street" ref={(el) => setSpotRef("section-powell-street", el)}>
              Powell Street Festival
            </h2>
            <p>
              <strong>Dates & Hours:</strong> August 2 – 3, 2025 (BC Day long weekend), ~11:30am – 7:00pm.
            </p>
            <p>
              <strong>Location:</strong> Oppenheimer Park{" "}
              <Marker
                coordY={49.28268}
                coordX={-123.09425}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , in Vancouver’s historic Japantown.
            </p>
            <p>
              <strong>What It’s About:</strong> A free community festival celebrating Japanese-Canadian 
              culture and cuisine. Known for incredible street food (yakisoba, takoyaki, spam musubi), 
              live taiko drumming, sumo demos, and more.
            </p>
            <p>
              <strong>Insider Tip:</strong> Bring sun protection and arrive early for sumo matches. 
              Food stalls are popular, so expect lines for the tastiest bites.
            </p>

            {/* Richmond Night Market */}
            <h2 id="section-richmond-night-market" ref={(el) => setSpotRef("section-richmond-night-market", el)}>
              Richmond Night Market
            </h2>
            <p>
              <strong>Dates & Hours:</strong> Late Spring to Early Fall 2025, weekends and holidays. 
              Typically, Fridays & Saturdays 7:00pm–12:00am; Sundays & holiday Mondays 7:00pm–11:00pm.
            </p>
            <p>
              <strong>Location:</strong> 8351 River Road, Richmond{" "}
              <Marker
                coordY={49.19561}
                coordX={-123.12594}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , near Bridgeport SkyTrain station. 
            </p>
            <p>
              <strong>What It’s About:</strong> North America’s largest night market, brimming with 
              Asian street food, shopping, carnival games, and live entertainment.
            </p>
            <p>
              <strong>Insider Tip:</strong> Arrive right at opening or buy a Zoom Pass to skip long 
              entry lines. The market is cash-oriented; bring small bills for faster transactions.
            </p>

            {/* Vancouver Christmas Market */}
            <h2 id="section-christmas-market" ref={(el) => setSpotRef("section-christmas-market", el)}>
              Vancouver Christmas Market
            </h2>
            <p>
              <strong>Dates & Hours:</strong> Mid-November – December 24, 2025, daily. Hours vary; 
              typically weekdays 4:00pm–9:30pm, weekends 11:00am–10:00pm.
            </p>
            <p>
              <strong>Location:</strong> Jack Poole Plaza{" "}
              <Marker
                coordY={49.28763}
                coordX={-123.11300}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              , next to the Olympic Cauldron.
            </p>
            <p>
              <strong>What It’s About:</strong> A German-style holiday market offering festive foods, 
              mulled wine, live entertainment, and handcrafted gifts in a cozy waterfront setting.
            </p>
            <p>
              <strong>Insider Tip:</strong> Dress warmly and buy tickets online to reserve a time 
              slot, especially on weekends. The collectible Glühwein mug makes a cute souvenir!
            </p>

            {/* Tips for Attendees */}
            <h2 id="tips" ref={(el) => setSpotRef("tips", el)}>Tips for Attendees</h2>
            <p>
              Plan your visits by checking festival websites or social media in advance. Arrive 
              early, bring cash for faster transactions, and consider sharing dishes so you can 
              taste more varieties. Use public transit when possible, as parking can be difficult. 
              Stay hydrated, dress for the weather, and don’t forget to properly dispose of waste.
            </p>

            {/* Local Regulations & Safety */}
            <h2 id="local-regulations" ref={(el) => setSpotRef("local-regulations", el)}>
              Local Regulations & Safety
            </h2>
            <p>
              Food vendors must follow strict health regulations; they’re inspected by Vancouver 
              Coastal Health. Alcohol must be consumed in designated areas, and ID is required if 
              you’re 19+. Smoking (including cannabis) is generally prohibited in festival grounds, 
              except possibly in designated areas. Many festivals are accessible, but large crowds 
              can be challenging. Keep valuables secure, use common sense in busy areas, and follow 
              official instructions if weather or safety advisories arise.
            </p>

            {/* FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these food festivals free?</h3>
              <p>
                Many street festivals (Greek Day, Italian Day) are free, while the Richmond Night 
                Market, Christmas Market, and certain tasting events charge admission. Check each 
                festival’s official site for up-to-date ticket info.
              </p>

              <h3>2. What happens if it rains?</h3>
              <p>
                Most events are rain or shine, and Vancouverites typically keep going under umbrellas. 
                Only severe weather might force a cancellation. Check the festival’s social media for 
                any last-minute changes.
              </p>

              <h3>3. Are pets allowed?</h3>
              <p>
                Pet policies vary by festival. Many outdoor street festivals allow dogs on leash, but 
                enclosed events often do not. Check specific guidelines before bringing your furry 
                friend.
              </p>
            </div>

            {/* Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>Conclusion</h2>
            <p>
              From winter warm-ups to summertime street parties and holiday wonderlands, Vancouver’s 
              2025 food festivals offer a delicious journey through the city’s multicultural spirit. 
              By preparing ahead and embracing each festival’s unique charm, you’ll enjoy an 
              unforgettable feast for all senses. Grab your friends, an empty stomach, and a sense 
              of adventure – Vancouver’s foodie scene awaits!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}